import { useCustomerBrandHotelSelection } from 'components/customerBrandHotelSelect/useCustomerBrandHotelSelection';
import { getItem } from 'helpers/storage';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export function MicroApp({url}: MicroAppProps) {
    const userData = getItem('user');
    const containerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeHeight, setIframeHeight] = useState(500);
    const [minHeight, setMinHeight] = useState(0);

    useEffect(() => {
        // Calculate the available viewport height
        const calculateMinHeight = () => {
            if (containerRef.current) {
                const containerRect = containerRef.current!.getBoundingClientRect();
                const availableHeight = window.innerHeight - containerRect.top - 20;
                setMinHeight(Math.max(500, availableHeight));
            }
        };

        calculateMinHeight();

        window.addEventListener('resize', calculateMinHeight);

        return () => window.removeEventListener('resize', calculateMinHeight);
    }, [containerRef.current, window.innerHeight]);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== new URL(url).origin) {
                return;
            }

            // Handle resize messages
            if (event.data && event.data.type === 'resize') {
                const newHeight = Math.max(event.data.height, minHeight); // Set minimum height
                setIframeHeight(newHeight);
            }

            if (event.data && event.data.type === 'scrollToTop') {
                window.scrollTo(0, 0);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [minHeight]);

    const {selectedBrandId, setBrandId, selectedHotelId, setHotelId, selectedBrand, selectedHotel} = useCustomerBrandHotelSelection();

    useEffect(() => {
        const handleRedirect = (event) => {
            if (event.origin !== new URL(url).origin) {
                return;
            }

            // Handle redirect messages
            if (event.data && event.data.type === 'redirect') {
                setBrandId(event.data.brandId);
                setHotelId(event.data.hotelId);
                window.location.href = event.data.url;
            }
        };

        window.addEventListener('message', handleRedirect);

        return () => window.removeEventListener('message', handleRedirect);
    }, []);

    const iframeUrl = useMemo(() => {
        // Determine the URL based on the environment
        const enrichedUrl = new URL(url);

        if (userData) {
            enrichedUrl.searchParams.append('token', userData.token);
        }
        if (selectedBrandId) {
            enrichedUrl.searchParams.append('brandId', selectedBrandId.toString());
            enrichedUrl.searchParams.append('brandName', selectedBrand.name);
        }
        if (selectedHotelId) {
            enrichedUrl.searchParams.append('hotelId', selectedHotelId.toString());
            enrichedUrl.searchParams.append('hotelName', selectedHotel.name);
        }

        return enrichedUrl;
    }, [url, selectedBrandId, selectedHotelId, userData, selectedBrand, selectedHotel]);

    return (
        <div ref={containerRef} className="my-lqa-container" style={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            height: `100%`,
        }}>
            <iframe
                ref={iframeRef}
                src={iframeUrl.href}
                title="My LQA Application"
                style={{
                    width: '100%',
                    height: `${iframeHeight}px`, // Explicit height based on content
                    border: 'none',
                    display: 'block',
                }}
            />
        </div>
    );
}

interface MicroAppProps {
    url: string;
}
